import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DecisionBadge } from "@/components/capital/AssetDecisionDialog";
import { Sparkles, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { CONSEQ, ConsequenceBadge } from "@/lib/capitalLabels";

const HZ_BADGE = {
  "Immediate Attention": "bg-rose-100 text-rose-700",
  "12 Month Plan": "bg-amber-100 text-amber-700",
};

export function PortalPriorities({ assets, onRespond }) {
  const rows = (assets || []).filter((a) => ["Immediate Attention", "12 Month Plan"].includes(a.horizon)).slice(0, 60);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="portal-priorities">
      <div className="px-4 py-3 border-b border-slate-100">
        <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">
          Priority assets — recommended for renewal planning ({rows.length})
        </span>
        <p className="text-[11px] text-slate-400 mt-0.5">Respond to each recommendation — your decisions feed the next planning cycle. A recommendation is not an approval.</p>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center">No assets currently need attention — your portfolio is in good shape.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left font-medium px-3 py-2">Asset</th>
                <th className="text-left font-medium px-3 py-2">Site</th>
                <th className="text-right font-medium px-3 py-2">Cond</th>
                <th className="text-left font-medium px-3 py-2">Timing</th>
                <th className="text-left font-medium px-3 py-2">Impact if it fails</th>
                <th className="text-right font-medium px-3 py-2">Est. cost</th>
                <th className="text-left font-medium px-3 py-2">Decision</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`portal-priority-${a.id}`}>
                  <td className="px-3 py-2 text-slate-800 max-w-[240px] truncate font-medium">{a.asset_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{a.site_name || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{a.condition ?? "—"}</td>
                  <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${HZ_BADGE[a.horizon] || "bg-slate-100 text-slate-600"}`}>{a.horizon === "Immediate Attention" ? "Beyond design life" : "12 months"}</span></td>
                  <td className="px-3 py-2">
                    <ConsequenceBadge a={a} testid={`portal-consequence-${a.id}`} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(a.cost)}</td>
                  <td className="px-3 py-2"><DecisionBadge status={a.decision_status} decision={a.client_decision} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onRespond(a)}
                            data-testid={`portal-respond-${a.id}`}>Respond</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PortalCapitalPlan({ clientName }) {
  const [scen, setScen] = useState(null);
  useEffect(() => {
    if (!clientName) return;
    api.get("/capital/scenarios-live", { params: { client: clientName }, timeout: 60000 })
      .then((r) => setScen(r.data)).catch(() => {});
  }, [clientName]);
  if (!scen) return <div className="h-40 bg-white border border-slate-200 rounded-lg shimmer" />;
  return (
    <div className="space-y-4" data-testid="portal-capital-plan">
      <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4">
        <div className="text-[10px] uppercase tracking-widest text-gbe font-medium">24-month renewal pool</div>
        <div className="mt-1 font-display text-2xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(scen.pool?.value)}</div>
        <div className="text-xs text-slate-600">{fmtNumber(scen.pool?.assets)} assets identified across the renewal horizons.</div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {(scen.scenarios || []).map((s) => (
          <div key={s.scenario} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`portal-scenario-${s.scenario.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="text-sm font-display font-semibold text-slate-900">{s.scenario}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 min-h-[32px]">{s.description}</div>
            <div className="mt-3 font-display text-xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(s.capital_required)}</div>
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              <li className="flex justify-between"><span>Assets funded</span><span className="tabular-nums font-medium">{fmtNumber(s.assets_included)}</span></li>
              <li className="flex justify-between"><span>Priority risk addressed</span><span className="tabular-nums font-medium">{s.priority_risk_addressed_pct}%</span></li>
              <li className="flex justify-between"><span>High-priority remaining</span><span className="tabular-nums font-medium">{fmtNumber(s.high_priority_remaining)}</span></li>
              <li className="flex justify-between"><span>Deferred exposure</span><span className="tabular-nums font-medium">{fmtCurrency(s.deferred_exposure)}</span></li>
            </ul>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">All figures reconcile to your live asset portfolio. Talk to your GBE account manager to lock in a program.</p>
    </div>
  );
}

const PORTAL_SAMPLES = [
  "What should we replace first and why?",
  "If we budget $100,000 next year, what does it cover?",
  "Which of our sites carries the most risk?",
];

export function PortalAskAI({ clientName }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = async (question) => {
    if (!question.trim()) return;
    setBusy(true); setAnswer(null);
    try {
      const r = await api.post("/capital/ai/ask", { client: clientName, question }, { timeout: 120000 });
      setAnswer(r.data.answer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="portal-ask-ai">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <h3 className="font-display text-base font-semibold text-slate-900">Ask the AI agent about your assets</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">Answers use only your organisation's data — it can explain rankings, budgets and what's changed.</p>
      <div className="mt-3 flex gap-2">
        <Textarea rows={2} value={q} onChange={(e) => setQ(e.target.value)} className="flex-1"
                  placeholder="e.g. What should we replace first and why?" data-testid="portal-ask-input" />
        <Button className="bg-violet-600 hover:bg-violet-700 text-white self-end" onClick={() => ask(q)} disabled={busy}
                data-testid="portal-ask-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {PORTAL_SAMPLES.map((s) => (
          <button key={s} onClick={() => { setQ(s); ask(s); }}
                  className="text-[11px] rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600 hover:border-violet-300 hover:text-violet-700">
            {s}
          </button>
        ))}
      </div>
      {busy && <div className="mt-3 text-sm text-slate-500">Analysing your portfolio…</div>}
      {answer && (
        <div className="mt-3 rounded-md border border-violet-200 bg-violet-50/30 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
             data-testid="portal-ask-answer"
             dangerouslySetInnerHTML={{ __html: answer.replace(/&/g, "&amp;").replace(/</g, "&lt;")
               .replace(/\*\*(.+?)\*\*/g, "<strong class='text-slate-900'>$1</strong>") }} />
      )}
    </div>
  );
}
